// components/SlotForms/AddNewSlotForm.jsx
import React, { useEffect, useRef, useState } from 'react';
import { Box, FormControlLabel, Checkbox, TextField, Grid, IconButton, Button, InputAdornment, Tooltip } from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import CustomAutocomplete from 'components/Fileds/CustomAutocomplete';
import CustomTimeTextField from 'components/Fileds/CustomTimeTextFiled';
import CustomTypography from 'components/Fileds/CustomTypography';
import CustomTextField from 'components/Fileds/CustomTextFiled';
import CustomButton from 'components/Fileds/CustomButton';
import theme from 'theme/ThemeContext';
import { DesktopDatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { useAddTimeSlotsMutation, useUpdateTimeSlotsMutation } from 'pages/timeSlots/services';
import AddIcon from '@mui/icons-material/Add';
import { isTimeValidForVendor } from 'utils/function';

const AddNewSlotForm = ({ categories, editData, handleCloseModal, onAddCategory, initialData, vendorTimings }) => {
    const anchorRef = useRef(null);
    const [open, setOpen] = useState(false);
    const [addLoading, setAddLoading] = useState(false);
    const [addTimeSlotsApi] = useAddTimeSlotsMutation();
    const [updateTimeSlotsApi] = useUpdateTimeSlotsMutation();


    const formik = useFormik({
        initialValues: {
            category: editData
                ? categories.find((cat) => cat.id === editData.category_id) || null
                : null,
            time_slot: editData?.time_slot ? dayjs(editData?.time_slot, 'hh:mmA') : null,
            discount: editData?.discount || null,
            is_weekend: editData?.is_weekend || false,
            auto_delete_time: editData?.auto_delete_time
                ? dayjs(editData.auto_delete_time)
                : null,
        },
        validationSchema: Yup.object({
            category: Yup.object().nullable().required('Category is Required'),
            time_slot: Yup.date()
                .nullable()
                .required('Time Slot is Required')
                .test('is-valid-time', 'Time must be within restaurant operating hours', function (value) {
                    if (!value || !vendorTimings) return true;
                    return isTimeValidForVendor(dayjs(value), vendorTimings, this.parent.is_weekend);
                }),
        }),
        // onSubmit: values => console.log(values,"valiess Add New Slot")
        onSubmit: async (vals) => {
            setAddLoading(true);
            try {
                const obj = {
                    category_id: vals.category.id,
                    // time_slot: dayjs(vals?.time_slot).toISOString(),
                    time_slot: dayjs(vals.time_slot).format('hh:mmA'),
                    discount: vals.discount,
                    is_weekend: vals.is_weekend,
                    auto_delete_time: vals.auto_delete_time
                        ? dayjs(vals.auto_delete_time)
                        : null,
                }
                if (editData?.id) {
                    await updateTimeSlotsApi({ id: editData.id, ...obj }).unwrap();
                } else {
                    await addTimeSlotsApi(obj).unwrap();
                }
                handleCloseModal();
                formik.resetForm();
            } catch (e) {
                console.error(e);
            } finally {
                setAddLoading(false);
            }
        },
    });

    useEffect(() => {
        if (initialData) {
            formik.setValues(initialData);
        }
    }, [initialData]);

    useEffect(() => {
        if (editData?.time_slot) {
            formik.setFieldValue('time_slot', dayjs(editData.time_slot, 'hh:mmA'), false);
        }
    }, [editData]);

    return (
        <Box key={editData?.id ?? 'new'} component="form" onSubmit={formik.handleSubmit} sx={{ padding: '0px 30px', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ mb: '20px' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                    <CustomTypography
                        variant="subtitle2"
                        label="Select Category"
                        sx={{ fontSize: '14px', fontWeight: 600, color: theme.palette.typographyLabel.main, mb: 0 }}
                    />
                    <Tooltip title="Add Category" arrow placement="top">
                        <IconButton
                            size="small"
                            onClick={() => onAddCategory && onAddCategory(formik.values, 'manual')}
                            sx={{
                                backgroundColor: theme.palette.primary.main,
                                color: theme.palette.common.white,
                                '&:hover': {
                                    backgroundColor: theme.palette.primary.dark,
                                },
                                width: 20,
                                height: 20,
                                ml: 1
                            }}
                        >
                            <AddIcon fontSize="small" sx={{ fontSize: 16 }} />
                        </IconButton>
                    </Tooltip>
                </Box>
                <CustomAutocomplete
                    name="category"
                    options={categories}
                    placeholder={'Time Slot Category'}
                    value={formik.values.category}
                    onChange={(_, val) => formik.setFieldValue('category', val)}
                    error={formik.touched.category && formik.errors.category}
                    helperText={formik.touched.category && formik.errors.category}
                    getOptionLabel={opt => opt.name || ''}
                />
            </Box>
            <Box sx={{ mb: '20px' }}>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={8}>
                        <CustomTypography variant="subtitle2" label="Time Slot Value"
                            sx={{ fontSize: '14px', fontWeight: 600, color: theme.palette.typographyLabel.main, mb: 0.5 }}
                        />
                        <CustomTimeTextField
                            name="time_slot"
                            value={formik.values.time_slot}
                            onChange={(newDate) => formik.setFieldValue('time_slot', newDate)}
                            error={formik.touched.time_slot && formik.errors.time_slot}
                            helperText={formik.touched.time_slot && formik.errors.time_slot}
                            shouldDisableTime={(timeValue) => !isTimeValidForVendor(timeValue, vendorTimings, formik.values.is_weekend)}
                        />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <CustomTypography variant="subtitle2" label="Discount (%)"
                            sx={{ fontSize: '14px', fontWeight: 600, color: theme.palette.typographyLabel.main, mb: 0.5 }}
                        />
                        <CustomTextField
                            fullWidth
                            name="discount"
                            placeholder={"Value (%)"}
                            label=""
                            value={formik.values.discount}
                            onChange={formik.handleChange}
                            error={formik.touched.discount && Boolean(formik.errors.discount)}
                            helperText={formik.touched.discount && formik.errors.discount}
                        />
                    </Grid>
                </Grid>
            </Box>

            <Box sx={{ mb: '15px' }}>
                <FormControlLabel
                    control={
                        <Checkbox
                            name="is_weekend"
                            checked={formik.values.is_weekend}
                            onChange={formik.handleChange}
                        />
                    }
                    label="Make it available on weekends only"
                />
            </Box>

            <Box sx={{ mb: '20px' }}>
                {/*  Label */}
                <CustomTypography variant="subtitle2" label="Auto Delete Time Slot after"
                    sx={{ fontSize: '14px', fontWeight: 600, color: theme.palette.typographyLabel.main, mb: 0.5 }}
                />

                {/*  1) DesktopDatePicker with its input completely hidden  */}
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DesktopDatePicker
                        open={open}
                        onClose={() => setOpen(false)}
                        value={formik.values.auto_delete_time}
                        onChange={(newDate) => {
                            // formik.handleChange(newDate);
                            formik.setFieldValue('auto_delete_time', newDate);
                            setOpen(false);
                        }}
                        // ===> this hides the default TextField completely
                        slots={{ textField: () => null }}
                        slotProps={{
                            popper: {
                                anchorEl: anchorRef.current,
                                placement: 'bottom-start',
                                disablePortal: false,     // keep it in the body portal
                                modifiers: [
                                    {
                                        name: 'offset',
                                        options: { offset: [0, 4] }, // a few pixels below
                                    },
                                    {
                                        name: 'preventOverflow',
                                        options: { boundary: 'viewport' },
                                    },
                                ],
                            },
                        }}
                    />

                    {/*  2) Your button, which is the *only* visible thing  */}
                    <Button
                        ref={anchorRef}
                        fullWidth
                        variant="outlined"
                        onClick={() => setOpen((prev) => !prev)}
                        sx={{
                            justifyContent: 'space-between',
                            textTransform: 'none',
                            color: theme.palette.text.primary,
                            borderColor: theme.palette.divider,
                            padding: '10px 14px',
                            height: '48px',
                            borderRadius: '8px',
                            fontWeight: 400,
                            fontSize: '16px',
                        }}
                        endIcon={<CalendarMonthIcon fontSize="small" />}
                    >
                        {formik.values.auto_delete_time
                            ? dayjs(formik.values.auto_delete_time).format('MM/DD/YYYY')
                            : 'Never Delete'}
                    </Button>
                </LocalizationProvider>
            </Box>
            <Box className='d-flex align-items-center justify-content-center'>
                <CustomButton
                    type="submit"
                    fullWidth
                    loading={addLoading}
                    disabled={addLoading}
                    sx={{ borderRadius: "10px", width: "180px", height: '45px' }}
                >
                    {editData ? "Update" : "Add New"}
                </CustomButton>
            </Box>
        </Box>
    );
};

export default AddNewSlotForm;